// ─── Bill Conflict View ──────────────────────────────────────────────────────
//
// G2.22: Production-grade conflict renderer for BILL sync conflicts.
// Brand-blue accent to match ENTITY_TYPE_CONFIG.BILL.tone = 'confirmed'.
//
// Traceability: FR-SYNC-013 (conflict resolution), US-BILL-018/019.

import { EmptyState } from '../../CompositeComponents/EmptyState';
import { GLASS_FORM, cn } from '../../../constants/design-tokens';
import { getBillSchema, formatValue, formatPaiseDecimal as formatPaise, computeDiscountPaise, computeLineTotal } from '@bbpos/utils';
import { resolveTaxRegime } from '../../../utils/tax-regime';
import type { TaxRegimePresentation } from '../../../utils/tax-regime';
import type { CurrencyCode } from '@bbpos/types';
import { GroupCard } from './GroupCard';
import { ConflictHeader } from './ConflictHeader';
import { ServerItemsEmptyState } from './ServerItemsEmptyState';

// ─── Types ───────────────────────────────────────────────────────────────────

interface BillItem {
  product_id?: string;
  name?: string;
  sku?: string;
  unit?: string;
  quantity?: number;
  unit_price?: number;
  unit_cost?: number;
  gst_rate?: number;
  hsn_code?: string;
  discount_type?: 'PERCENTAGE' | 'FLAT';
  discount_value?: number;
  [extra: string]: unknown;
}

interface BillSplitPayment {
  payment_method?: string;
  amount?: number;
  [extra: string]: unknown;
}

export interface BillConflictViewProps {
  clientVersion: Record<string, unknown>;
  serverVersion: Record<string, unknown>;
  deviceId: string | null;
  conflictMessage?: string;
  /**
   * Resolved tax regime (from `@bbpos/ui`'s `resolveTaxRegime` /
   * `useTaxRegime()`). India keeps the CGST/SGST/IGST schema rows + HSN
   * column + `GST` header (byte-identical); GCC collapses to a single VAT
   * total row, drops the HSN column and relabels the tax column. Defaults to
   * the India (`INR`) regime. `@bbpos/ui` stays hook-free — consumers pass
   * this in from `useTaxRegime()`.
   */
  taxRegime?: TaxRegimePresentation;
  /**
   * Tenant currency for money fields/columns. Defaults to `'INR'` so the
   * schema, line-item amounts and split payments are byte-identical to the
   * pre-multi-currency behaviour; non-INR threads the correct symbol/subunit/
   * decimals (3-decimal GCC currencies render 3 decimals). `@bbpos/ui` stays
   * hook-free — consumers pass this in from `useTenantCurrency()`.
   */
  currency?: CurrencyCode;
}

// ─── Helpers ─────────────────────────────────────────────────────────────────

function coerceItems(value: unknown): BillItem[] {
  if (!Array.isArray(value)) return [];
  return value.filter((it): it is BillItem => !!it && typeof it === 'object');
}

function coerceSplitPayments(value: unknown): BillSplitPayment[] {
  if (!Array.isArray(value)) return [];
  return value.filter((p): p is BillSplitPayment => !!p && typeof p === 'object');
}

// ─── Items table ─────────────────────────────────────────────────────────────

function ItemsTable({
  items,
  side,
  taxName,
  showHsn,
  currency,
}: {
  items: BillItem[];
  side: 'client' | 'server';
  taxName: string;
  showHsn: boolean;
  currency: CurrencyCode;
}) {
  if (items.length === 0) {
    return (
      <EmptyState
        variant="table"
        title={side === 'client' ? 'No items in client payload' : 'No items in server record'}
      />
    );
  }

  return (
    <div className={cn(GLASS_FORM, 'overflow-x-auto')}>
      <table className="w-full text-xs">
        <caption className="sr-only">
          Bill line items from the {side === 'client' ? 'offline device' : 'server record'}
        </caption>
        <thead>
          <tr className="text-[10px] uppercase tracking-wider text-neutral-400 dark:text-neutral-500">
            <th className="text-left px-3 py-2 font-medium">Product</th>
            {showHsn && <th className="text-left px-3 py-2 font-medium">HSN</th>}
            <th className="text-right px-3 py-2 font-medium">Qty</th>
            <th className="text-right px-3 py-2 font-medium">Unit Price</th>
            <th className="text-right px-3 py-2 font-medium">Discount</th>
            <th className="text-right px-3 py-2 font-medium">{taxName}</th>
            <th className="text-right px-3 py-2 font-medium">Line Total</th>
          </tr>
        </thead>
        <tbody>
          {items.map((item, idx) => {
            const discount = computeDiscountPaise(item);
            const lineTotal = computeLineTotal(item);
            return (
              <tr
                key={item.product_id ?? `row-${idx}`}
                className={cn(
                  'transition-colors',
                  idx % 2 === 1 && 'bg-neutral-50/60 dark:bg-neutral-800/40',
                  'animate-slide-up',
                )}
                style={{ animationDelay: `${Math.round(Math.log2(idx + 1) * 100)}ms` }}
              >
                <td className="px-3 py-2 text-neutral-800 dark:text-neutral-200">
                  <div className="flex flex-col">
                    <span className="font-medium">{item.name ?? '\u2014'}</span>
                    {item.sku && (
                      <span className="text-[10px] font-mono text-neutral-500 dark:text-neutral-400">
                        {item.sku}
                      </span>
                    )}
                  </div>
                </td>
                {showHsn && (
                  <td className="px-3 py-2 font-mono text-neutral-600 dark:text-neutral-400">
                    {item.hsn_code ?? '\u2014'}
                  </td>
                )}
                <td className="px-3 py-2 text-right font-mono tabular-nums text-neutral-700 dark:text-neutral-300">
                  {Number(item.quantity ?? 0)}
                  {item.unit && (
                    <span className="ml-1 text-[10px] uppercase text-neutral-400">{item.unit}</span>
                  )}
                </td>
                <td className="px-3 py-2 text-right font-mono tabular-nums text-neutral-700 dark:text-neutral-300">
                  {formatPaise(item.unit_price, currency)}
                </td>
                <td className="px-3 py-2 text-right font-mono tabular-nums text-neutral-700 dark:text-neutral-300">
                  {formatPaise(discount, currency)}
                  {item.discount_type === 'PERCENTAGE' && (
                    <span className="ml-1 text-[9px] uppercase text-neutral-400">
                      {Number(item.discount_value ?? 0)}%
                    </span>
                  )}
                </td>
                <td className="px-3 py-2 text-right font-mono tabular-nums text-neutral-600 dark:text-neutral-400">
                  {item.gst_rate != null ? `${item.gst_rate}%` : '\u2014'}
                </td>
                <td className="px-3 py-2 text-right font-mono tabular-nums font-semibold text-neutral-900 dark:text-neutral-100">
                  {formatPaise(lineTotal, currency)}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

// ─── Split payments table ────────────────────────────────────────────────────

function SplitPaymentsTable({
  payments,
  side,
  currency,
}: {
  payments: BillSplitPayment[];
  side: 'client' | 'server';
  currency: CurrencyCode;
}) {
  const total = payments.reduce((sum, p) => sum + Number(p.amount ?? 0), 0);

  return (
    <div className={cn(GLASS_FORM, 'overflow-x-auto')}>
      <table className="w-full text-xs">
        <caption className="sr-only">
          Split payment breakdown from the {side === 'client' ? 'offline device' : 'server record'}
        </caption>
        <thead>
          <tr className="text-[10px] uppercase tracking-wider text-neutral-400 dark:text-neutral-500">
            <th className="text-left px-3 py-2 font-medium">Method</th>
            <th className="text-right px-3 py-2 font-medium">Amount</th>
          </tr>
        </thead>
        <tbody>
          {payments.map((p, idx) => (
            <tr
              key={`split-${idx}`}
              className={cn(
                'transition-colors',
                idx % 2 === 1 && 'bg-neutral-50/60 dark:bg-neutral-800/40',
              )}
            >
              <td className="px-3 py-2 text-neutral-800 dark:text-neutral-200 font-medium uppercase tracking-wide">
                {formatValue('enum', p.payment_method)}
              </td>
              <td className="px-3 py-2 text-right font-mono tabular-nums text-neutral-900 dark:text-neutral-100">
                {formatPaise(p.amount, currency)}
              </td>
            </tr>
          ))}
          <tr className="bg-neutral-100/40 dark:bg-neutral-800/30">
            <td className="px-3 pt-3 pb-2 text-[10px] uppercase tracking-wider text-neutral-500 dark:text-neutral-400 font-semibold">
              Total tendered
            </td>
            <td className="px-3 py-2 text-right font-mono tabular-nums font-bold text-neutral-900 dark:text-neutral-100">
              {formatPaise(total, currency)}
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  );
}

// ─── Main view ───────────────────────────────────────────────────────────────

export function BillConflictView({
  clientVersion,
  serverVersion,
  deviceId,
  conflictMessage,
  taxRegime,
  currency = 'INR',
}: BillConflictViewProps) {
  // Hook-free: default to India (INR) so the schema, HSN column and `GST`
  // header are byte-identical to the pre-multi-currency behaviour.
  const regime = taxRegime ?? resolveTaxRegime('INR');
  const schema = getBillSchema(regime);
  const billNumber =
    (clientVersion.bill_number as string | undefined) ??
    (serverVersion.bill_number as string | undefined) ??
    'Unknown';

  const statusRaw =
    (clientVersion.status as string | undefined) ??
    (serverVersion.status as string | undefined);

  const items = coerceItems(clientVersion.items);
  const splitPayments = coerceSplitPayments(clientVersion.payments);
  const serverItems = coerceItems(serverVersion.items);
  const serverItemsLoaded = 'items' in serverVersion;
  const serverPayments = coerceSplitPayments(serverVersion.payments);
  const serverPaymentsLoaded = 'payments' in serverVersion;
  const hasSplit = splitPayments.length >= 2 || serverPayments.length >= 2;

  return (
    <div className="space-y-4 animate-fade-in" data-testid="bill-conflict-view">
      <ConflictHeader
        entityLabel="Bill Conflict"
        identifier={billNumber}
        accent={{
          from: 'from-brand-500/10',
          to: 'to-indigo-500/10',
          ring: 'ring-brand-500/20',
          text: 'text-brand-700',
          darkText: 'dark:text-brand-300',
        }}
        tone="brand"
        badgeVariant="confirmed"
        badgeLabel={statusRaw ? formatValue('enum', statusRaw) : undefined}
        deviceId={deviceId}
        conflictMessage={conflictMessage}
        ariaLabel="Bill conflict header"
      />

      {/* Grouped field comparison */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        {schema.groups.map((group, i) => (
          <GroupCard
            key={group.title}
            title={group.title}
            fields={group.fields}
            clientVersion={clientVersion}
            serverVersion={serverVersion}
            currency={currency}
            delay={i * 60}
          />
        ))}
      </div>

      {/* Items section */}
      <section className="space-y-3" role="region" aria-label="Bill line items comparison">
        <div className="flex items-center justify-between">
          <h4 className="text-xs font-semibold uppercase tracking-wider text-neutral-500 dark:text-neutral-400">
            Line Items
          </h4>
          <span className="text-[10px] uppercase tracking-wider text-neutral-400 dark:text-neutral-500">
            {items.length} {items.length === 1 ? 'item' : 'items'}
          </span>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 items-start">
          <div>
            <div className="mb-1.5 text-[10px] uppercase tracking-wider text-emerald-600 dark:text-emerald-400">
              Client (offline device)
            </div>
            <ItemsTable
              items={items}
              side="client"
              taxName={regime.taxName}
              showHsn={regime.showHsn}
              currency={currency}
            />
          </div>
          <div>
            <div className="mb-1.5 text-[10px] uppercase tracking-wider text-sky-600 dark:text-sky-400">
              Server
            </div>
            {serverItemsLoaded ? (
              <ItemsTable
                items={serverItems}
                side="server"
                taxName={regime.taxName}
                showHsn={regime.showHsn}
                currency={currency}
              />
            ) : (
              <ServerItemsEmptyState label="line items" />
            )}
          </div>
        </div>
      </section>

      {/* Split payments */}
      {hasSplit && (
        <section className="space-y-3" role="region" aria-label="Bill split payments comparison">
          <div className="flex items-center justify-between">
            <h4 className="text-xs font-semibold uppercase tracking-wider text-neutral-500 dark:text-neutral-400">
              Split Payments
            </h4>
            <span className="text-[10px] uppercase tracking-wider text-neutral-400 dark:text-neutral-500">
              {Math.max(splitPayments.length, serverPayments.length)} tenders
            </span>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 items-start">
            <div>
              <div className="mb-1.5 text-[10px] uppercase tracking-wider text-emerald-600 dark:text-emerald-400">
                Client (offline device)
              </div>
              {splitPayments.length > 0 ? (
                <SplitPaymentsTable payments={splitPayments} side="client" currency={currency} />
              ) : (
                <EmptyState
                  variant="table"
                  title="No split tenders in client payload"
                />
              )}
            </div>
            <div>
              <div className="mb-1.5 text-[10px] uppercase tracking-wider text-sky-600 dark:text-sky-400">
                Server
              </div>
              {serverPaymentsLoaded ? (
                serverPayments.length > 0 ? (
                  <SplitPaymentsTable payments={serverPayments} side="server" currency={currency} />
                ) : (
                  <EmptyState
                    variant="table"
                    title="No split tenders in server record"
                  />
                )
              ) : (
                <ServerItemsEmptyState label="split payments" />
              )}
            </div>
          </div>
        </section>
      )}
    </div>
  );
}
